import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './LoginPage.css';
import { emailDomain, logoMark, orgName } from '../brand';

// Same rule as the shared account store (org_accounts.MIN_PASSWORD_LENGTH).
export const MIN_PASSWORD_LENGTH = 10;

function passwordProblem(password, confirm) {
  if (password.length < MIN_PASSWORD_LENGTH) return `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
  if (password !== confirm) return 'Passwords do not match';
  return null;
}

function CardBrand({ tagline }) {
  return (
    <div className="login-brand">
      <img className="login-favicon" src={logoMark()} alt={orgName()} />
      <div className="login-tagline">{tagline}</div>
    </div>
  );
}

/**
 * "Forgot password?" — asks for an email and always answers the same way, so
 * the form can't be used to find out who has an account.
 */
function ForgotPasswordForm({ initialEmail = '', onBack, backLabel = 'Back to sign in' }) {
  const [email, setEmail] = useState(initialEmail.includes('@') ? initialEmail : '');
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await apiService.forgotPassword(email.trim());
      setSent(result.message || 'If an account uses that address, we’ve emailed it a link to choose a new password.');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="login-panel">
        <h2>Check your email</h2>
        <div className="login-success">{sent}</div>
        <p className="login-intro">Didn’t get it? Check your spam folder, or try again in a few minutes.</p>
        <div className="login-meta">
          <button type="button" className="link-btn" onClick={onBack}>{backLabel}</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <h2>Forgot your password?</h2>
      <p className="login-intro">Enter your email and we’ll send you a link to choose a new one.</p>
      <label>Email
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          placeholder={`email@${emailDomain()}`}
          required
          autoFocus
        />
      </label>
      {error && <div className="login-error">{error}</div>}
      <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send me a link'}</button>
      <div className="login-meta">
        <button type="button" className="link-btn" onClick={onBack}>{backLabel}</button>
      </div>
    </form>
  );
}

/**
 * LoginPage — username/password form against the backend's /auth/login.
 * Shown by AuthGate whenever the user is not authenticated.
 *
 * On success, the backend sets the session cookie; we call onAuthenticated()
 * so AuthGate re-checks state and reveals the app.
 *
 * Signing up takes an email only: the password is chosen on the page the
 * emailed link opens (/set-password), so only the inbox owner can set it.
 */
export default function LoginPage({ onAuthenticated }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'check-email' | 'forgot'
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const go = (next) => { setMode(next); setError(null); setSuccessMsg(null); };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await apiService.login(username.trim(), password);
      // Successful login sets the cookie; tell parent to re-check.
      onAuthenticated(result);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setBusy(true);
    try {
      const result = await apiService.register(email.trim());
      setSuccessMsg(result.message || 'Check your email for a link to finish setting up your account.');
      setMode('check-email');
    } catch (err) {
      setError(err.message || 'Sign-up failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-aurora" aria-hidden="true" />
      <div className="login-card">
        <CardBrand tagline="Support Plan Organizer" />

        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            {successMsg && <div className="login-success">{successMsg}</div>}

            {/* SSO placeholder — non-functional, here to demonstrate the
                planned enterprise sign-in flow for stakeholder review. */}
            <button
              type="button"
              className="sso-btn"
              onClick={(e) => { e.preventDefault(); /* placeholder */ }}
              title="Single sign-on via your organizational account"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Sign in with SSO</span>
            </button>

            <div className="login-divider"><span>or sign in with username</span></div>

            <label>
              Username or email
              <span className="input-wrap">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  placeholder={`email@${emailDomain()}`}
                  required
                  autoFocus
                />
              </span>
            </label>
            <label>
              Password
              <span className="input-wrap">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  required
                />
              </span>
            </label>
            <div className="login-forgot">
              <button type="button" className="link-btn" onClick={() => go('forgot')}>
                Forgot password?
              </button>
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
            <div className="login-meta">
              Need an account?{' '}
              <button type="button" className="link-btn" onClick={() => go('register')}>
                Sign up
              </button>
            </div>
          </form>
        )}

        {mode === 'register' && (
          <form onSubmit={handleRegister}>
            <h2>Create your account</h2>
            <p className="login-intro">
              Enter your work email. We’ll email you a link to choose your password.
              One account works across all of {orgName()}’s apps.
            </p>
            <label>Work email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder={`email@${emailDomain()}`}
                required
                autoFocus
              />
            </label>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send me a link'}</button>
            <div className="login-meta">
              Already have an account?{' '}
              <button type="button" className="link-btn" onClick={() => go('login')}>
                Sign in
              </button>
            </div>
          </form>
        )}

        {mode === 'check-email' && (
          <div className="login-panel">
            <h2>Check your email</h2>
            <div className="login-success">{successMsg}</div>
            <p className="login-intro">
              Open the link in the email to choose your password.
              Didn’t get it? Check your spam folder, or try again in a few minutes.
            </p>
            <div className="login-meta">
              <button type="button" className="link-btn" onClick={() => go('login')}>
                Back to sign in
              </button>
            </div>
          </div>
        )}

        {mode === 'forgot' && (
          <ForgotPasswordForm initialEmail={username} onBack={() => go('login')} />
        )}
      </div>
    </div>
  );
}


/**
 * SetPasswordPage — /set-password?token=… opened from an emailed link:
 * finishing a sign-up, accepting an invitation, or resetting a forgotten
 * password. Rendered outside AuthGate. On success the backend signs the
 * person in and we land on the home screen.
 */
export function SetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';

  const [link, setLink] = useState(null); // null while checking, then {valid, purpose, email}
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setLink({ valid: false }); return undefined; }
    apiService.linkInfo(token)
      .then((info) => { if (!cancelled) setLink(info); })
      .catch(() => { if (!cancelled) setLink({ valid: false }); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const problem = passwordProblem(password, confirm);
    if (problem) { setError(problem); return; }
    setBusy(true);
    try {
      await apiService.setPassword(token, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not set your password');
      setBusy(false);
    }
  };

  const toSignIn = () => navigate('/', { replace: true });
  const isReset = link?.purpose === 'reset';

  let body;
  if (link === null) {
    body = <p className="login-intro">Checking your link…</p>;
  } else if (forgot) {
    body = <ForgotPasswordForm initialEmail={link.email || ''} onBack={toSignIn} />;
  } else if (!link.valid) {
    body = (
      <div className="login-panel">
        <h2>This link can’t be used</h2>
        <p className="login-intro">
          It has expired or has already been used. Links to reset a password last one hour;
          sign-up links last 48 hours and invitations 7 days, and each works once.
        </p>
        <p className="login-intro">
          Forgot your password? Ask for a new reset link below. Were you signing up or
          invited? Sign up again with the same address from the sign-in page.
        </p>
        <button type="button" className="login-primary" onClick={() => setForgot(true)}>
          Send me a new reset link
        </button>
        <div className="login-meta">
          <button type="button" className="link-btn" onClick={toSignIn}>Back to sign in</button>
        </div>
      </div>
    );
  } else {
    body = (
      <form onSubmit={submit}>
        {isReset ? (
          <>
            <h2>Choose a new password</h2>
            <p className="login-intro">
              For <strong>{link.email}</strong>. At least {MIN_PASSWORD_LENGTH} characters.
              Saving it signs you out of {orgName()}’s apps everywhere else.
            </p>
          </>
        ) : (
          <>
            <h2>Set a password for <span className="login-email">{link.email}</span></h2>
            <p className="login-intro">
              At least {MIN_PASSWORD_LENGTH} characters. This one account and password work
              across all of {orgName()}’s apps.
            </p>
          </>
        )}
        <input type="email" value={link.email} autoComplete="username" readOnly hidden />
        <label>{isReset ? 'New password' : 'Password'}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            autoFocus
          />
        </label>
        <label>Confirm password
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
          />
        </label>
        {error && <div className="login-error">{error}</div>}
        <button type="submit" disabled={busy}>
          {busy ? 'Saving…' : isReset ? 'Save new password' : 'Set password and sign in'}
        </button>
        {error && (
          <div className="login-meta">
            <button type="button" className="link-btn" onClick={() => setForgot(true)}>
              Send me a new link
            </button>
          </div>
        )}
      </form>
    );
  }

  return (
    <div className="login-page">
      <div className="login-aurora" aria-hidden="true" />
      <div className="login-card">
        <CardBrand tagline={isReset ? 'Reset your password' : 'Set up your account'} />
        {body}
      </div>
    </div>
  );
}


/**
 * ChangePasswordPage — shown when the account must change its password
 * (e.g. first login as the seeded admin).
 */
export function ChangePasswordPage({ onChanged }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) { setError('New passwords do not match'); return; }
    if (next.length < MIN_PASSWORD_LENGTH) { setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`); return; }
    setBusy(true);
    try {
      await apiService.changePassword(current, next);
      onChanged();
    } catch (err) {
      setError(err.message || 'Password change failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <CardBrand tagline="Set a new password" />
        <form onSubmit={submit}>
          <h2>Change password</h2>
          <p className="login-meta">
            You must set a new password before you can use the app.
          </p>
          <label>Current password
            <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoFocus />
          </label>
          <label>New password
            <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={MIN_PASSWORD_LENGTH} />
          </label>
          <label>Confirm new password
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={MIN_PASSWORD_LENGTH} />
          </label>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save password'}</button>
        </form>
      </div>
    </div>
  );
}
